// /home/labdoo/zitech-nexus/frontend/src/components/Website/Navbar.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAnnouncements } from '../../contexts/AnnouncementContext';
import './Navbar.css';

// Define the Announcement type
interface Announcement {
  id: string | number;
  title: string;
  message: string;
  icon?: string;
  image?: string;
  image_url?: string;
  link?: string;
  link_text?: string;
  type?: string;
  priority?: string;
  is_active: boolean;
  start_date: string;
  end_date?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  created_by_name?: string;
  is_currently_active?: boolean;
}

// Define Firework Particle types
interface FireworkParticle {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  life: number;
  size: number;
  opacity: number;
  isBackground?: boolean;
}

const Navbar: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const [activeAnnouncementsList, setActiveAnnouncementsList] = useState<Announcement[]>([]);
  const [jobCount, setJobCount] = useState(0);
  const [fireworkParticles, setFireworkParticles] = useState<FireworkParticle[]>([]);
  const [isAnnouncementVisible, setIsAnnouncementVisible] = useState(true);
  const animationRef = useRef<number | null>(null);
  const announcementBarRef = useRef<HTMLDivElement | null>(null);
  const burstIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { activeAnnouncement, announcements, allActiveAnnouncements } = useAnnouncements();

  // Fetch job count
  useEffect(() => {
    const fetchJobCount = async () => {
      try {
        const response = await fetch('https://zitechsolutions.co.tz/api/jobs/jobs/');
        if (response.ok) {
          const data = await response.json();
          const count = data.results ? data.results.length : data.length || 0;
          setJobCount(count);
          localStorage.setItem('job_count', count.toString());
        } else {
          const cached = localStorage.getItem('job_count');
          if (cached) {
            setJobCount(parseInt(cached));
          }
        }
      } catch (error) {
        console.error('Error fetching job count:', error);
        const cached = localStorage.getItem('job_count');
        if (cached) {
          setJobCount(parseInt(cached));
        }
      }
    };

    fetchJobCount();
    const interval = setInterval(fetchJobCount, 300000);
    return () => clearInterval(interval);
  }, []);

  // Initialize background particles
  useEffect(() => {
    if (!announcementBarRef.current) return;
    
    const rect = announcementBarRef.current.getBoundingClientRect();
    const barWidth = rect.width || 800;
    const barHeight = rect.height || 60;
    
    const colors = [
      'rgba(255, 215, 0, 0.3)',
      'rgba(255, 255, 255, 0.2)',
      'rgba(255, 165, 0, 0.25)',
      'rgba(255, 69, 0, 0.2)',
      'rgba(255, 215, 0, 0.15)',
    ];
    
    const bgParticles: FireworkParticle[] = [];
    for (let i = 0; i < 40; i++) {
      const size = 2 + Math.random() * 6;
      bgParticles.push({
        id: Date.now() + i,
        x: Math.random() * barWidth,
        y: Math.random() * barHeight,
        vx: (Math.random() - 0.5) * 0.3,
        vy: -0.1 - Math.random() * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 0.5 + Math.random() * 0.5,
        size: size,
        opacity: 0.2 + Math.random() * 0.3,
        isBackground: true,
      });
    }
    setFireworkParticles(bgParticles);
  }, []);

  // Animate particles
  useEffect(() => {
    if (fireworkParticles.length === 0) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const animateParticles = () => {
      setFireworkParticles(prev => {
        const updated = prev
          .map(p => {
            if (p.isBackground) {
              return {
                ...p,
                x: p.x + p.vx,
                y: p.y + p.vy,
                life: p.life,
                opacity: 0.2 + Math.sin(Date.now() / 2000 + p.id) * 0.15,
              };
            }
            return {
              ...p,
              x: p.x + p.vx * 0.025,
              y: p.y + p.vy * 0.025,
              vy: p.vy + 0.15,
              life: p.life - 0.012,
              size: p.size * 0.99,
              opacity: p.life,
            };
          })
          .filter(p => {
            if (p.isBackground) return true;
            return p.life > 0 && p.size > 0.3;
          });

        // Only add new background particles if announcement is visible
        const bgCount = updated.filter(p => p.isBackground).length;
        if (bgCount < 40 && announcementBarRef.current && isAnnouncementVisible) {
          const rect = announcementBarRef.current.getBoundingClientRect();
          const colors = [
            'rgba(255, 215, 0, 0.3)',
            'rgba(255, 255, 255, 0.2)',
            'rgba(255, 165, 0, 0.25)',
            'rgba(255, 69, 0, 0.2)',
            'rgba(255, 215, 0, 0.15)',
          ];
          const newBg = [];
          for (let i = 0; i < 2; i++) {
            newBg.push({
              id: Date.now() + i + Math.random() * 1000,
              x: Math.random() * rect.width,
              y: Math.random() * rect.height,
              vx: (Math.random() - 0.5) * 0.3,
              vy: -0.1 - Math.random() * 0.3,
              color: colors[Math.floor(Math.random() * colors.length)],
              life: 0.5 + Math.random() * 0.5,
              size: 2 + Math.random() * 6,
              opacity: 0.2 + Math.random() * 0.3,
              isBackground: true,
            });
          }
          return [...updated, ...newBg];
        }

        return updated;
      });

      animationRef.current = requestAnimationFrame(animateParticles);
    };

    animationRef.current = requestAnimationFrame(animateParticles);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [fireworkParticles.length, isAnnouncementVisible]);

  // Continuous fireworks burst - stops when announcement is hidden
  useEffect(() => {
    if (!announcementBarRef.current || activeAnnouncementsList.length === 0) return;

    const triggerFireworkBurst = () => {
      // Don't trigger if announcement is not visible
      if (!isAnnouncementVisible) return;
      if (!announcementBarRef.current) return;
      
      const rect = announcementBarRef.current.getBoundingClientRect();
      const barWidth = rect.width;
      const barHeight = rect.height;
      
      const colors = [
        '#ff6b6b', '#ff4757', '#ff6348', '#ff7f50',
        '#feca57', '#ff9f43', '#f368e0', '#ff9ff3',
        '#48dbfb', '#54a0ff', '#2e86de', '#1dd1a1',
        '#5f27cd', '#341f97', '#ee5a24', '#00d2d3',
        '#ff6b81', '#f8a5c2', '#74b9ff', '#55efc4',
        '#ffd93d', '#6bcb77', '#4d96ff', '#ff6b6b'
      ];
      
      const numBursts = 2 + Math.floor(Math.random() * 2);
      let allNewParticles: FireworkParticle[] = [];
      
      for (let b = 0; b < numBursts; b++) {
        const centerX = Math.random() * barWidth;
        const centerY = Math.random() * barHeight;
        const particleCount = 30 + Math.floor(Math.random() * 30);
        
        for (let i = 0; i < particleCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const velocity = 80 + Math.random() * 200;
          const color = colors[Math.floor(Math.random() * colors.length)];
          const size = 3 + Math.random() * 10;
          
          allNewParticles.push({
            id: Date.now() + b + i + Math.random() * 10000,
            x: centerX,
            y: centerY,
            vx: Math.cos(angle) * velocity * (0.5 + Math.random()),
            vy: Math.sin(angle) * velocity * (0.5 + Math.random()) - 30,
            color: color,
            life: 0.7 + Math.random() * 0.3,
            size: size,
            opacity: 1,
            isBackground: false,
          });
        }
      }
      
      setFireworkParticles(prev => {
        const bg = prev.filter(p => p.isBackground);
        const alive = prev.filter(p => !p.isBackground && p.life > 0.1);
        return [...bg, ...alive, ...allNewParticles];
      });
      
      setTimeout(() => {
        setFireworkParticles(prev => {
          const filtered = prev.filter(p => p.isBackground || p.life > 0.1);
          if (filtered.length > 500) {
            const bg = filtered.filter(p => p.isBackground);
            const active = filtered.filter(p => !p.isBackground).slice(0, 100);
            return [...bg, ...active];
          }
          return filtered;
        });
      }, 3000);
    };

    // Initial burst
    setTimeout(triggerFireworkBurst, 500);
    
    // Burst every 3 seconds - only if announcement is visible
    burstIntervalRef.current = setInterval(() => {
      if (isAnnouncementVisible) {
        triggerFireworkBurst();
      }
    }, 3000);

    return () => {
      if (burstIntervalRef.current) {
        clearInterval(burstIntervalRef.current);
        burstIntervalRef.current = null;
      }
    };
  }, [activeAnnouncementsList.length, isAnnouncementVisible]);

  // Check if announcement bar is visible in viewport
  useEffect(() => {
    const checkVisibility = () => {
      if (!announcementBarRef.current) return;
      
      const rect = announcementBarRef.current.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      // Only update if visibility state changes
      if (isVisible !== isAnnouncementVisible) {
        setIsAnnouncementVisible(isVisible);
        
        // If announcement becomes hidden, clear all non-background particles
        if (!isVisible) {
          setFireworkParticles(prev => prev.filter(p => p.isBackground));
        }
      }
    };

    // Check visibility on scroll
    window.addEventListener('scroll', checkVisibility, { passive: true });
    // Check visibility on resize
    window.addEventListener('resize', checkVisibility, { passive: true });
    // Initial check
    setTimeout(checkVisibility, 100);

    return () => {
      window.removeEventListener('scroll', checkVisibility);
      window.removeEventListener('resize', checkVisibility);
    };
  }, [isAnnouncementVisible]);

  // Get all active announcements
  useEffect(() => {
    let announcementsList: Announcement[] = [];
    
    if (allActiveAnnouncements && allActiveAnnouncements.length > 0) {
      announcementsList = allActiveAnnouncements;
    } else if (announcements && announcements.length > 0) {
      const now = new Date().toISOString();
      announcementsList = announcements.filter(
        (a: Announcement) => a.is_active && 
        a.start_date <= now && 
        (!a.end_date || a.end_date >= now)
      );
    } else if (activeAnnouncement) {
      announcementsList = [activeAnnouncement as Announcement];
    }
    
    setActiveAnnouncementsList(announcementsList);
  }, [allActiveAnnouncements, announcements, activeAnnouncement]);

  // Auto-scroll through announcements
  useEffect(() => {
    if (activeAnnouncementsList.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentAnnouncementIndex((prevIndex) => 
        (prevIndex + 1) % activeAnnouncementsList.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [activeAnnouncementsList.length]);

  // SCROLL HANDLER
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      if (location.pathname === '/') {
        const sectionIds = ['home', 'services', 'technologies', 'agile-methodology', 'why-choose-us', 'about', 'portfolio', 'contact'];
        let currentSection = 'home';
        let minDistance = Infinity;
        
        for (const id of sectionIds) {
          const element = document.getElementById(id);
          if (element) {
            const rect = element.getBoundingClientRect();
            const distance = Math.abs(rect.top - 100);
            if (distance < minDistance) {
              minDistance = distance;
              currentSection = id;
            }
          }
        }
        
        setActiveSection(currentSection);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    setTimeout(handleScroll, 100);
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const menuItems = [
    { name: 'Home', link: 'home' },
    { name: 'Services', link: 'services' },
    { name: 'Technologies', link: 'technologies' },
    { name: 'Methodology', link: 'agile-methodology' },
    { name: 'Why Choose Us', link: 'why-choose-us' },
    { name: 'About', link: 'about' },
    { name: 'Portfolio', link: 'portfolio' },
    { name: 'Contact', link: 'contact' },
  ];

  const topHeaderLinks = [
    { name: 'Support', link: '/support' },
    { name: 'Blog', link: '/blog' },
    { name: 'Careers', link: '/careers' },
  ];

  const isHomePage = location.pathname === '/';

  const handleNavigation = (link: string) => {
    if (isHomePage) {
      const element = document.getElementById(link);
      if (element) {
        const offset = 70;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    } else {
      navigate(`/#${link}`);
    }
    setMobileOpen(false);
  };

  const handleLogoClick = () => {
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveSection('home');
    } else {
      navigate('/');
    }
  };

  const currentAnnouncement = activeAnnouncementsList.length > 0 
    ? activeAnnouncementsList[currentAnnouncementIndex] 
    : null;

  const showDots = activeAnnouncementsList.length > 1;

  const announcementsWithImages = activeAnnouncementsList.filter(a => a.image_url);

  const getTypeColor = (type?: string) => {
    const colors = {
      info: '#3b82f6',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      promotion: '#8b5cf6'
    };
    return colors[type as keyof typeof colors] || colors.info;
  };

  const Icons = {
    Phone: () => (
      <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.362 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
      </svg>
    ),
    Email: () => (
      <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <path d="M22 6l-10 7L2 6"/>
      </svg>
    ),
    Location: () => (
      <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
    Menu: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 12H21M3 6H21M3 18H21" strokeLinecap="round"/>
      </svg>
    ),
    Close: () => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 6L6 18M6 6L18 18" strokeLinecap="round"/>
      </svg>
    ),
  };

  return (
    <>
      {fireworkParticles.length > 0 && (
        <div className="fireworks-overlay">
          {fireworkParticles.map((p) => (
            <div
              key={p.id}
              className={`firework-particle ${p.isBackground ? 'background-particle' : 'active-particle'}`}
              style={{
                left: p.x + 'px',
                top: p.y + 'px',
                width: p.size + 'px',
                height: p.size + 'px',
                backgroundColor: p.color,
                opacity: p.opacity || p.life,
                transform: p.isBackground ? 'none' : `scale(${Math.max(0.3, p.life)})`,
                borderRadius: '50%',
                position: 'absolute',
                pointerEvents: 'none',
                transition: 'none',
                boxShadow: p.isBackground ? 'none' : `0 0 ${p.size * 2}px ${p.color}60, 0 0 ${p.size * 4}px ${p.color}30`,
              }}
            />
          ))}
        </div>
      )}

      <div className="top-header">
        <div className="container">
          <div className="top-header-content">
            <div className="top-header-left">
              <span className="contact-item">
                <Icons.Phone />
                +255 768 494 444
              </span>
              <span className="contact-item">
                <Icons.Email />
                info@zitechsolutions.com
              </span>
              <span className="contact-item">
                <Icons.Location />
                Mwanza, Tanzania
              </span>
            </div>
            <div className="top-header-right">
              {topHeaderLinks.map((link) => (
                <a key={link.name} href={link.link} className="top-link">
                  {link.name}
                  {link.name === 'Careers' && jobCount > 0 && (
                    <span className="job-count-badge">{jobCount}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {activeAnnouncementsList.length > 0 && (
        <div 
          ref={announcementBarRef}
          className="announcement-bar fireworks-active announcement-desktop-only"
        >
          <div className="container">
            <div className="announcement-content">
              <div className="announcement-display">
                <div className="announcement-display-content">
                  {currentAnnouncement && (
                    <>
                      <span className="announcement-display-icon">📢</span>
                      <span className="announcement-display-text">
                        {currentAnnouncement.message}
                      </span>
                      {currentAnnouncement.link && (
                        <a href={currentAnnouncement.link} className="announcement-display-link">
                          {currentAnnouncement.link_text || 'Learn More'} →
                        </a>
                      )}
                    </>
                  )}
                </div>
                {showDots && (
                  <div className="announcement-nav-dots">
                    {activeAnnouncementsList.map((_, index: number) => (
                      <span
                        key={index}
                        className={`dot ${index === currentAnnouncementIndex ? 'active' : ''}`}
                        onClick={() => setCurrentAnnouncementIndex(index)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {currentAnnouncement?.image_url && (
              <div className="image-dropdown-panel">
                <div className="image-dropdown-header">
                  <span className="image-dropdown-title">📷 Announcement Image</span>
                  <span className="image-dropdown-total">
                    {currentAnnouncementIndex + 1} / {announcementsWithImages.length}
                  </span>
                </div>
                <div 
                  className="image-dropdown-item"
                  onClick={() => {
                    if (currentAnnouncement.image_url) {
                      window.open(currentAnnouncement.image_url, '_blank');
                    }
                  }}
                >
                  <img 
                    src={currentAnnouncement.image_url} 
                    alt={currentAnnouncement.title || 'Announcement image'}
                    className="image-dropdown-img"
                    loading="lazy"
                  />
                  <div className="image-dropdown-caption">
                    <span className="image-dropdown-title-text">{currentAnnouncement.title}</span>
                    <span className="image-dropdown-type" style={{ backgroundColor: getTypeColor(currentAnnouncement.type) }}>
                      {currentAnnouncement.type || 'info'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="navbar-content">
            <div className="logo" onClick={handleLogoClick}>
              <img src="/zitech_logo.jpeg" alt="ZiTech Solutions" className="logo-img" />
            </div>

            <div className="nav-menu">
              {menuItems.map((item) => (
                <button
                  key={item.name}
                  className={`nav-link ${isHomePage && activeSection === item.link ? 'active' : ''}`}
                  onClick={() => handleNavigation(item.link)}
                >
                  {item.name}
                </button>
              ))}
              {user ? (
                <button className="btn-primary" onClick={() => navigate('/dashboard')}>
                  Dashboard
                </button>
              ) : (
                <button className="btn-outline" onClick={() => navigate('/login')}>
                  Client Portal
                </button>
              )}
            </div>

            <button className="menu-toggle" onClick={() => setMobileOpen(true)}>
              <Icons.Menu />
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="drawer-overlay" onClick={() => setMobileOpen(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <img src="/zitech_logo.jpeg" alt="ZiTech Solutions" className="drawer-logo" />
              <button className="drawer-close" onClick={() => setMobileOpen(false)}>
                <Icons.Close />
              </button>
            </div>
            
            <div className="drawer-body">
              <div className="drawer-section">
                <h4 className="drawer-label">Quick Links</h4>
                {topHeaderLinks.map((link) => (
                  <a key={link.name} href={link.link} className="drawer-link">
                    {link.name}
                    {link.name === 'Careers' && jobCount > 0 && (
                      <span className="job-count-badge">{jobCount}</span>
                    )}
                  </a>
                ))}
              </div>

              <div className="drawer-divider"></div>

              <div className="drawer-section">
                <h4 className="drawer-label">Navigation</h4>
                {menuItems.map((item) => (
                  <button
                    key={item.name}
                    className={`drawer-link ${isHomePage && activeSection === item.link ? 'active' : ''}`}
                    onClick={() => handleNavigation(item.link)}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="drawer-footer">
              {user ? (
                <button className="btn-primary full-width" onClick={() => {
                  setMobileOpen(false);
                  navigate('/dashboard');
                }}>
                  Dashboard
                </button>
              ) : (
                <button className="btn-outline full-width" onClick={() => {
                  setMobileOpen(false);
                  navigate('/login');
                }}>
                  Client Portal
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;